// third-party
import { createSlice } from '@reduxjs/toolkit';

// project imports
import { dispatch } from '../index';
import * as userAPI from 'api/user';
import * as productivityGoalAPI from 'api/productivityGoal';

const initialState = {
    accessPermissionsList: {
        error: false,
        loaded: false,
        data: []
    },
    activeOrgMembersList: {
        error: false,
        loaded: false,
        data: []
    },
    // org roster × every day of one month, goals derived by the server's resolver:
    // { month, today, days: [...], weeks: [{ startDate, dates }], users: [{ userId, name, email, onPlan,
    //   overExempted, days: { [date]: { goalHours, gross, removed, workingDay, reasons, secondsLogged } } }] }
    // `days` is exactly the month; `weeks` are the full Mon–Sun weeks whose Monday is in the month, so
    // each user's `days` also carries the few dates past month end that close the last week.
    // `month` is the month that was asked for (YYYY-MM, or CURRENT_MONTH); `data.month`
    // is the month the server answered with. `reportsTo` is the Manager filter it was
    // asked with (a manager's user id, 'none', or null for everyone).
    orgMonthGoalProgress: {
        error: false,
        loaded: false,
        month: null,
        reportsTo: null,
        data: {
            month: null,
            today: null,
            days: [],
            weeks: [],
            users: []
        }
    },
    userDetail: {
        error: false,
        loaded: false,
        data: {
            id: 0,
            firstName: '',
            lastName: '',
            email: '',
            disabled: false,
            supportUser: false,
            orgGrants: [],
            // the user's manager: { userId, name, disabled }, or null
            reportsTo: null,
            clinicGrants: {},
            providerClinicMapping: [],
            // null if not a provider account
            providerId: null,
            // if has the provider clinic matching for selected clinic
            assignedToClinic: true,
            primaryCarePhysician: false,
            specialist: false,
            npi: ''
        }
    }
};

const slice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        setUserPermissionsListError(state) {
            state.accessPermissionsList.error = true;
            state.accessPermissionsList.loaded = true;
        },
        setUserPermissionsList(state, action) {
            state.accessPermissionsList.error = false;
            state.accessPermissionsList.loaded = true;
            state.accessPermissionsList.data = action.payload;
        },
        setActiveOrgMembersListError(state) {
            state.activeOrgMembersList.error = true;
            state.activeOrgMembersList.loaded = true;
        },
        setOrgMonthGoalProgressLoading(state, action) {
            state.orgMonthGoalProgress.error = false;
            state.orgMonthGoalProgress.loaded = false;
            state.orgMonthGoalProgress.month = action.payload.month;
            state.orgMonthGoalProgress.reportsTo = action.payload.reportsTo;
        },
        setOrgMonthGoalProgressError(state) {
            state.orgMonthGoalProgress.error = true;
            state.orgMonthGoalProgress.loaded = true;
        },
        setOrgMonthGoalProgress(state, action) {
            // ignore a late response for a month or manager the user has already switched away from
            const isCurrentRequest =
                action.payload.requestedMonth === state.orgMonthGoalProgress.month &&
                action.payload.requestedReportsTo === state.orgMonthGoalProgress.reportsTo;
            if (!isCurrentRequest) {
                return;
            }
            state.orgMonthGoalProgress.error = false;
            state.orgMonthGoalProgress.loaded = true;
            state.orgMonthGoalProgress.data = action.payload.data;
        },
        setActiveOrgMembersList(state, action) {
            state.activeOrgMembersList.error = false;
            state.activeOrgMembersList.loaded = true;
            state.activeOrgMembersList.data = action.payload;
        },
        setUserDetail(state, action) {
            state.userDetail.error = false;
            state.userDetail.loaded = true;
            state.userDetail.data = action.payload;
        },
        setUserDetailError(state, action) {
            state.userDetail.error = action.payload;
            state.userDetail.loaded = true;
        },
        resetUserDetailState(state) {
            state.userDetail = initialState.userDetail;
        },
        resetActiveOrgMembersListState(state) {
            state.activeOrgMembersList = initialState.activeOrgMembersList;
        },
        resetAccessPermissionsListState(state) {
            state.accessPermissionsList = initialState.accessPermissionsList;
        },
        resetOrgMonthGoalProgressState(state) {
            state.orgMonthGoalProgress = initialState.orgMonthGoalProgress;
        }
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function getUserPermissionList() {
    return async () => {
        try {
            const response = await userAPI.fetchUsersPermissionList();
            dispatch(slice.actions.setUserPermissionsList(response.data.users));
        } catch (error) {
            dispatch(slice.actions.setUserPermissionsListError(error));
        }
    };
}

export function getUserDetail(userId) {
    return async () => {
        try {
            const response = await userAPI.getUserDetail(userId);
            dispatch(slice.actions.setUserDetail(response.data.user));
        } catch (error) {
            dispatch(slice.actions.setUserDetailError(error));
        }
    };
}

export function resetUserDetail() {
    dispatch(slice.actions.resetUserDetailState());
}

export function resetActiveOrgMembersList() {
    dispatch(slice.actions.resetActiveOrgMembersListState());
}

export function resetUserPermissionList() {
    dispatch(slice.actions.resetAccessPermissionsListState());
}

export function resetOrgMonthGoalProgress() {
    dispatch(slice.actions.resetOrgMonthGoalProgressState());
}

// Ask for the server's current month (in the org zone) rather than a YYYY-MM the
// client resolved from its own clock.
export const CURRENT_MONTH = 'current';

// `reportsTo`: a manager's user id, 'none', or null for the whole roster
export function getOrgMonthGoalProgress(month, reportsTo = null) {
    return async () => {
        dispatch(slice.actions.setOrgMonthGoalProgressLoading({ month, reportsTo }));
        try {
            const response = await productivityGoalAPI.fetchOrgMonthGoalProgress(
                month === CURRENT_MONTH ? undefined : month,
                reportsTo ?? undefined
            );
            if (response.status !== 200) {
                throw new Error(response.message);
            }
            dispatch(slice.actions.setOrgMonthGoalProgress({ requestedMonth: month, requestedReportsTo: reportsTo, data: response.data }));
        } catch (error) {
            dispatch(slice.actions.setOrgMonthGoalProgressError(error));
        }
    };
}

export function getActiveOrgMembersList() {
    return async () => {
        try {
            const response = await userAPI.fetchActiveOrgMembersList();
            dispatch(slice.actions.setActiveOrgMembersList(response.data.users));
        } catch (error) {
            dispatch(slice.actions.setActiveOrgMembersListError(error));
        }
    };
}
